import {useLoadMore} from '@/common-pages/hooks/load-more.hooks';
import DetailNavTitle from '@/components/business/detail-nav-title';
import {cn, copyText, goBack, toPriceStr} from '@/utils';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getWeekCardBuyRecord} from '../vip.service';
import {Button, Image, InfiniteScroll} from 'antd-mobile';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import dayjs from 'dayjs';
type RecordStatusType = 'PROCESS' | 'COMPLETE' | 'FAILED' | 'REVIEW';
import {ReactComponent as CopyIcon} from '@components/assets/icons/proxy/home/copy.svg';
import NoData from '@/components/basic/error-pages/no-data';

const RecordStatusRow = ({status}: {status: RecordStatusType}) => {
  const {t} = useTranslation();
  const colors = useMemo(() => {
    const map = {
      PROCESS: {
        icon: null,
        text: t('label.inProgress'),
        bgImage:
          'linear-gradient(90deg, rgba(23, 118, 255, 0.08) 0.04%, rgba(23, 118, 255, 0.00) 99.94%)',
        color: '#1776FF',
      },
      COMPLETE: {
        icon: null,
        text: t('label.complete'),
        bgImage:
          'linear-gradient(90deg, rgba(3, 201, 58, 0.08) 0.04%, rgba(3, 201, 58, 0.00) 99.94%)',
        color: '#009919',
      },
      FAILED: {
        icon: null,
        text: t('label.failed'),
        bgImage:
          'linear-gradient(90deg, rgba(201, 3, 3, 0.08) 0.04%, rgba(201, 3, 3, 0.00) 99.94%)',
        color: '#C90303',
      },
      REVIEW: {
        icon: null,
        text: t('label.review'),
        bgImage:
          'linear-gradient(90deg, rgba(23, 118, 255, 0.08) 0.04%, rgba(23, 118, 255, 0.00) 99.94%)',
        color: '#1776FF',
      },
    };
    return map[status];
  }, [status, t]);
  return (
    <div
      className="flex flex-row items-center justify-between px-3 h-7 rounded-l-full"
      style={{
        background: colors.bgImage,
      }}>
      <span className="text-xs uppercase" style={{color: colors.color}}>
        {colors.text}
      </span>
      {status === 'PROCESS' && (
        <span className="text-xs capitalize text-t3">
          {t('label.within24Hours')}
        </span>
      )}
    </div>
  );
};

interface WeekBuyRecordItemProps {
  icon: string;
  title: string;
  money: string;
  status: RecordStatusType;
  defaultShowCount?: number;
  otherOptions: {
    label: string;
    value: string;
    canCopy?: boolean;
  }[];
}

const WeekBuyRecordItem = ({
  status,
  icon,
  title,
  money,
  otherOptions,
  defaultShowCount = 3,
}: WeekBuyRecordItemProps) => {
  const [showMore, setShowMore] = useState(false);
  return (
    <div className="flex flex-col bg-white rounded-lg py-2 px-3">
      <RecordStatusRow status={status} />
      <div
        className="flex flex-row items-center justify-between h-10"
        onClick={() => setShowMore(!showMore)}>
        <div className="flex flex-row items-center gap-3">
          <Image src={icon} className="size-6" />
          <span className="text-t2 text-sm font-bold capitalize">{title}</span>
        </div>
        <div className="flex flex-row items-center gap-2">
          <span
            className={cn(
              'text-base font-bold capitalize font-din',
              status === 'COMPLETE' || status === 'PROCESS'
                ? 'text-t1'
                : 'text-t4',
            )}>
            {money}
          </span>
          {otherOptions.length > defaultShowCount ? (
            <div
              className={cn(
                'size-3.5 bg-full bg-no-repeat',
                showMore ? 'rotate-180' : 'rotate-0',
              )}
              style={{
                backgroundImage: `url(${require('@/components/assets/icons/records-list-item/arrow-down.webp')})`,
              }}
            />
          ) : null}
        </div>
      </div>
      {(showMore ? otherOptions : otherOptions.slice(0, defaultShowCount)).map(
        item => (
          <div
            key={item.label}
            className="flex flex-row items-center justify-between h-10 text-xs">
            <span className="text-t3">{item.label}</span>
            <div className="text-t2 flex flex-row items-center gap-2">
              {item.canCopy ? (
                <>
                  <span>{item.value}</span>
                  <Button
                    className="bg-transparent p-0"
                    onClick={() => copyText(item.value)}>
                    <CopyIcon className="size-3.5" />
                  </Button>
                </>
              ) : (
                item.value
              )}
            </div>
          </div>
        ),
      )}
    </div>
  );
};

const WeekBuyRecord = () => {
  const {t} = useTranslation();
  const pageSize = 10;
  const {list, loading, hasMore, loadMore, reload} = useLoadMore(pageNo => {
    return getWeekCardBuyRecord(pageNo).then(res => {
      return {
        list: res,
        total:
          res.length > 0
            ? res.length < pageSize
              ? 0
              : pageSize * pageNo + 1
            : 0,
      };
    });
  }, pageSize);
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex flex-col w-full h-[100vh] bg-bg">
      <DetailNavTitle
        title={t('vip.label.weekCardBuyRecord')}
        onBack={() => goBack()}
        hideAmount
        hideServer
      />
      <Spin loading={loading} style={[theme.flex.flex1]}>
        <div className="flex flex-col p-3 gap-3 flex-1 overflow-y-auto">
          {list.map(item => (
            <WeekBuyRecordItem
              icon={require('@assets/icons/records/withdraw-new.webp')}
              title={item.cardName || ''}
              money={toPriceStr(item.price)}
              status={item.statusName as RecordStatusType}
              otherOptions={[
                {
                  label: t('label.purchaseTime'),
                  value: item.createTime
                    ? dayjs(item.createTime).format('DD/MM/YYYY hh:mm A')
                    : '-',
                },
                {
                  label: t('label.orderNo'),
                  value: item.orderNo || '-',
                  canCopy: true,
                },
              ]}
            />
          ))}
          {list.length > 0 ? (
            <InfiniteScroll hasMore={hasMore} loadMore={loadMore}>
              {hasMore ? t('label.loading') : t('label.noMore')}
            </InfiniteScroll>
          ) : (
            <NoData />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default WeekBuyRecord;
